'use client';

import { useIntl } from 'react-intl';
import { useState } from 'react';

import { RatingFeedbackDocument } from '@packages/gql/generated/shopping/RatingFeedbackDocument';

import { Popover, PopoverContent, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';

import { customerReviewCardActionsMenuMessages } from './CustomerReviewCardActionsMenu.messages';

/* GraphQL */ `
  mutation RatingFeedback($feedback: RatingFeedbackType!, $locale: String!, $reviewId: Int!) {
    ratingFeedback(feedback: $feedback, locale: $locale, reviewId: $reviewId)
  }
`;

export type CustomerReviewCardActionsMenuProps = {
  /**
   * ratingId - a unique rating id used to report a rating
   */
  ratingId: number;
};

/**
 * The CustomerReviewCardActionsMenu component displays a menu with user actions
 *
 * actions available:
 * - report rating
 */
export const CustomerReviewCardActionsMenu = ({ ratingId }: CustomerReviewCardActionsMenuProps) => {
  const { formatMessage } = useIntl();
  const { locale } = useI18n();
  const { addNotification } = useNotification();

  const [menuOpen, setMenuOpen] = useState(false);

  const [state, setRatingFeedback] = useAuthMutation(RatingFeedbackDocument);

  const actionHandler = async () => {
    try {
      const { data: ratingFeedbackData, error: ratingFeedbackError } = await setRatingFeedback({
        feedback: 'NOT_APPROPRIATE',
        reviewId: ratingId,
        locale,
      });
      if (ratingFeedbackError || !ratingFeedbackData?.ratingFeedback) {
        throw ratingFeedbackError || new Error('ratingFeedback is false');
      }
      addNotification({
        severity: 'success',
        duration: 3000,
        message: formatMessage(customerReviewCardActionsMenuMessages.actionsMenuReportSuccess),
      });
    } catch (err) {
      logger.error(err, 'Error during giving report');
      addNotification({
        severity: 'error',
        duration: 3000,
        message: formatMessage(customerReviewCardActionsMenuMessages.actionsMenuReportError),
      });
    } finally {
      setMenuOpen(false);
    }
  };

  return (
    <div className="text-size-body-sm">
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="icon-flat"
            aria-label={formatMessage(customerReviewCardActionsMenuMessages.actionsMenuAriaLabel)}
          >
            <span className="icon-emp-menu-android" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="px-0">
          <Button
            disabled={state.fetching}
            variant="icon-flat"
            className="w-auto rounded-none px-1"
            onClick={actionHandler}
          >
            {formatMessage(customerReviewCardActionsMenuMessages.actionsMenuReportText)}
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
